// @flow
import React, { Component, type Node } from 'react';
import moment from 'moment';
import { noop, sortBy } from 'lodash';

import { generateUniqueId } from 'Utils/unique-id/uniqueId';
import { type ValidationState } from 'Store/trip-information/kids/constants';

import { FormControl } from 'Components/frameworks/form-inputs/form-control/FormControl.jsx';

import { themes } from './InputDate.themes';

const DAY = 'd';
const MONTH = 'm';
const YEAR = 'y';

const DAY_LENGTH = 2;
const MONTH_LENGTH = 2;
const YEAR_LENGTH = 4;

type Props = {
  className?: string,
  dateFormat?: string,
  hasClear?: boolean,
  iconLeft?: IconName,
  iconRight?: IconName,
  id?: string,
  isDisabled?: boolean,
  isFilled?: boolean,
  isLoading?: boolean,
  isReadOnly?: boolean,
  isRequired?: boolean,
  label?: string | Node,
  name?: string,
  notes?: string,
  onBlur?: (SyntheticEvent<>) => void,
  onChange?: (name: string, value: timestamp | boolean | null) => Promise<void>,
  onClear?: (SyntheticEvent<>) => void,
  onClick?: (SyntheticEvent<>) => void,
  onFocus?: (SyntheticEvent<>) => void,
  onKeyDown?: (SyntheticKeyboardEvent<>) => void,
  onKeyUp?: (SyntheticKeyboardEvent<>) => void,
  placeholderDay?: string,
  placeholderMonth?: string,
  placeholderYear?: string,
  size?: SizeProps,
  theme?: string,
  validationState?: ValidationState,
  value?: timestamp | null,
};

type State = {
  dayValue: string,
  monthValue: string,
  yearValue: string,
};

function getInputsState({ value }: Props) {
  const date = value ? moment(value) : moment(null);

  return {
    dayValue: date.isValid() ? date.format('DD') : '',
    monthValue: date.isValid() ? date.format('MM') : '',
    yearValue: date.isValid() ? date.format('YYYY') : '',
  };
}

export class InputDate extends Component<Props, State> {
  static defaultProps = {
    dateFormat: 'DDMMYYYY',
    onChange: () => Promise.resolve(),
    size: 'medium',
    theme: 'default',
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      ...getInputsState(props),
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (this.props.value !== nextProps.value) {
      this.setState(getInputsState(nextProps));
    }
  }

  getDate() {
    const { dayValue, monthValue, yearValue } = this.state;

    const allFields =
      dayValue.length === DAY_LENGTH &&
      monthValue.length === MONTH_LENGTH &&
      yearValue.length === YEAR_LENGTH;

    if (allFields) {
      const date = moment([
        parseInt(yearValue, 10),
        parseInt(monthValue, 10) - 1,
        parseInt(dayValue, 10),
      ]);
      return date.isValid() ? date.valueOf() : false;
    }

    return null;
  }

  onChange = (inputRef: string, value: string, maxLength: number, nextRef: ?string) => {
    const { isLoading, isReadOnly, onChange = noop } = this.props;

    if (!isReadOnly && !isLoading) {
      this.setState(
        {
          [inputRef]: value,
        },
        () => onChange(this.props.name || '', this.getDate()),
      );

      if (nextRef && value.length === maxLength) {
        // $FlowFixMe
        this[nextRef].focus();
      }
    }
  };

  onClear = (e: SyntheticEvent<>) => {
    const { onClear = noop } = this.props;
    this.setState(
      {
        dayValue: '',
        monthValue: '',
        yearValue: '',
      },
      () => onClear(e),
    );
  };

  getInputOrder(type: string) {
    const { dateFormat = '' } = this.props;
    return dateFormat.toLowerCase().indexOf(type);
  }

  getInputs() {
    return [
      {
        ref: 'dayValue',
        placeholder: this.props.placeholderDay || '',
        value: this.state.dayValue,
        order: this.getInputOrder(DAY),
        maxLength: DAY_LENGTH,
      },
      {
        ref: 'monthValue',
        placeholder: this.props.placeholderMonth || '',
        value: this.state.monthValue,
        order: this.getInputOrder(MONTH),
        maxLength: MONTH_LENGTH,
      },
      {
        ref: 'yearValue',
        placeholder: this.props.placeholderYear || '',
        value: this.state.yearValue,
        order: this.getInputOrder(YEAR),
        maxLength: YEAR_LENGTH,
      },
    ];
  }

  render() {
    const {
      className,
      hasClear,
      iconLeft,
      iconRight,
      id = generateUniqueId(
        [typeof this.props.label === 'string' ? this.props.label : ''],
        'InputDate_',
      ),
      isDisabled,
      isFilled,
      isLoading,
      isReadOnly,
      isRequired,
      label,
      notes,
      onBlur,
      onClick,
      onFocus,
      onKeyDown,
      onKeyUp,
      size,
      theme,
      validationState,
    } = this.props;

    const { dayValue, monthValue, yearValue } = this.state;

    const hasValue = Boolean(dayValue) || Boolean(monthValue) || Boolean(yearValue);
    const hasClearEnabled = Boolean(hasClear && hasValue);

    const inputs = sortBy(this.getInputs(), ['order']);

    const { thInput, thInputWrapper } = themes[theme](this.props);

    return (
      <FormControl
        className={className}
        dir="ltr"
        hasClear={hasClearEnabled}
        iconLeft={iconLeft}
        iconRight={iconRight}
        id={id}
        isDisabled={isDisabled}
        isFilled={isFilled && hasValue}
        isLoading={isLoading}
        isReadOnly={isReadOnly}
        isRequired={isRequired}
        label={label}
        notes={notes}
        onClear={this.onClear}
        size={size}
        theme={theme}
        validationState={validationState}
      >
        {inputs.map((input, index) => {
          const nextRef = index + 1 < inputs.length ? inputs[index + 1].ref : null;

          return (
            <span key={index} className={thInputWrapper}>
              <input
                className={thInput}
                data-testid={input.ref}
                disabled={isDisabled}
                {...(index === 0 ? { id } : null)}
                maxLength={input.maxLength}
                onBlur={onBlur}
                onChange={e => this.onChange(input.ref, e.target.value, input.maxLength, nextRef)}
                onClick={onClick}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                placeholder={input.placeholder}
                readOnly={Boolean(isReadOnly || isLoading)}
                ref={node => {
                  // $FlowFixMe
                  this[input.ref] = node;
                }}
                required={isRequired}
                type="tel"
                value={input.value}
              />
            </span>
          );
        })}
      </FormControl>
    );
  }
}
